/**
 * Parallel generation jobs for one allow-listed account (emong111).
 *
 * That account may start a new one-click or workbook generation while other
 * jobs run; each stays tracked in the global progress widget. The server
 * enforces the matching per-user cap exemption
 * (my-flask-app/services/generation_concurrency_exemption.py). Same-workbook
 * exclusivity is unchanged for everyone.
 */

const PARALLEL_GENERATION_USERS = ['emong111', 'emong111@naver.com'];

type UserIdentity = { username?: string | null; email?: string | null } | null | undefined;

export function canRunParallelGenerationJobs(user: UserIdentity): boolean {
  if (!user) return false;
  return [user.username, user.email]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .some((v) => PARALLEL_GENERATION_USERS.includes(v.trim().toLowerCase()));
}

interface TrackedJobsView {
  isGenerating: boolean;
  shouldPoll: boolean;
  workbookId: number | null;
  parallelJobs?: ReadonlyArray<{ workbookId: number | null; status: string }>;
}

const LIVE_PARALLEL_STATUSES = new Set(['queued', 'processing', 'orphaned']);

/** True while any tracked job (primary card or parallel row) still runs for this workbook. */
export function isWorkbookJobTracked(context: TrackedJobsView, workbookId: number): boolean {
  if (context.isGenerating && context.shouldPoll && context.workbookId === workbookId) return true;
  return (context.parallelJobs ?? []).some(
    (job) => job.workbookId === workbookId && LIVE_PARALLEL_STATUSES.has(job.status),
  );
}

/** True while the progress card tracks a running job of another workbook (never for the exempt account). */
export function isOtherWorkbookJobTracked(
  context: TrackedJobsView,
  workbookId: number,
  user: UserIdentity,
): boolean {
  return (
    !canRunParallelGenerationJobs(user) &&
    context.isGenerating &&
    context.shouldPoll &&
    context.workbookId !== null &&
    context.workbookId !== workbookId
  );
}
